import { foldForSearch } from "@/shared/utils/searchText";

import { ALL_PHONE_COUNTRIES } from "./phone-countries";

/**
 * Countries whose phone number a person may enter.
 *
 * ## Why this is NOT the onboarding country list
 *
 * Where somebody can be served and where their phone number was issued are different questions. A resident of
 * Colombia may perfectly well carry a US or Canadian number, so the residence allowlist
 * (`ONBOARDING_COUNTRY_ISO2`, 17 countries) is the wrong gate for a phone field — and it was the one every
 * phone field used, which is exactly the bug this list exists to fix. The two lists overlap but neither
 * contains the other: this one adds the US, Canada and the Caribbean; the residence one keeps them out.
 *
 * ## How the list was chosen
 *
 * Product + compliance curation, delivered as `lista_telefonos.json`. It is an allowlist for the same reason
 * the residence one is: it fails closed, so a country becomes selectable only when somebody adds it here on
 * purpose. Out of the 218 in the catalog it leaves out the sanctioned jurisdictions, the offshore financial
 * centres (Cayman, BVI, Bermuda, Gibraltar, Curaçao, …) and the conflict / high-risk zones.
 *
 * Nicaragua is a deliberate asymmetry: excluded from residence, included here, because receiving a call at a
 * Nicaraguan number is not an operation.
 *
 * ## Order
 *
 * Regions are listed in product order — LatAm first, then the rest — which is the half of the request that
 * "priorizar los países de LATAM antes del listado general" names. Within a region the order is NOT frozen
 * here: it is computed per locale by `phoneCountryOptions`, because the source list was sorted by English
 * name and that reads mis-sorted in Spanish ("Croacia · Chipre · Chequia").
 */
export interface PhoneCountryRegion {
  /** Stable identifier. Not rendered today — the order is what carries the meaning. */
  id: string;
  iso2: readonly string[];
}

export const PHONE_COUNTRY_REGIONS: readonly PhoneCountryRegion[] = [
  {
    id: "latam",
    iso2: ["CO", "AR", "BO", "BR", "CL", "CR", "EC", "SV", "GT", "HN", "MX", "NI", "PA", "PY", "PE", "UY"],
  },
  { id: "caribbean", iso2: ["AG", "BS", "BB", "BZ", "DM", "GD", "GY", "JM", "KN", "LC", "VC", "SR", "TT"] },
  { id: "north_america", iso2: ["CA", "US"] },
  {
    id: "europe",
    iso2: [
      "AL",
      "AD",
      "AT",
      "BE",
      "BA",
      "BG",
      "HR",
      "CY",
      "CZ",
      "DK",
      "EE",
      "FI",
      "FR",
      "DE",
      "GR",
      "HU",
      "IS",
      "IE",
      "IT",
      "LV",
      "LI",
      "LT",
      "LU",
      "MT",
      "MC",
      "ME",
      "NL",
      "MK",
      "NO",
      "PL",
      "PT",
      "RO",
      "SM",
      "RS",
      "SK",
      "SI",
      "ES",
      "SE",
      "CH",
      "TR",
      "GB",
      "VA",
    ],
  },
  {
    id: "asia",
    iso2: [
      "AE",
      "BH",
      "BD",
      "BT",
      "IN",
      "ID",
      "JP",
      "JO",
      "KZ",
      "KW",
      "MY",
      "NP",
      "OM",
      "PH",
      "QA",
      "SA",
      "SG",
      "KR",
      "LK",
      "TH",
      "UZ",
      "VN",
    ],
  },
  {
    id: "oceania",
    iso2: ["AU", "FJ", "KI", "MH", "FM", "NR", "NZ", "PW", "PG", "WS", "SB", "TO", "TV", "VU"],
  },
  {
    id: "africa",
    iso2: [
      "DZ",
      "BJ",
      "BW",
      "CV",
      "CI",
      "GH",
      "GA",
      "MA",
      "MU",
      "NA",
      "RW",
      "SN",
      "SC",
      "ZA",
      "ST",
      "TG",
      "TN",
    ],
  },
];

/**
 * Pinned to the top of its region whatever the locale's alphabet says. Coloca's home market, and the value
 * every form preselects — keeping it first means the default is also the first thing the list shows.
 */
export const HOME_MARKET_ISO2 = "CO";

/** Flat membership list, in region order. Use `phoneCountryOptions` to render; this is for checks. */
export const PHONE_COUNTRY_ISO2: readonly string[] = PHONE_COUNTRY_REGIONS.flatMap((r) => r.iso2);

const PHONE_COUNTRY_ISO2_SET: ReadonlySet<string> = new Set(PHONE_COUNTRY_ISO2);

/** True when a phone number from this ISO 3166-1 alpha-2 country may be entered. */
export function isPhoneCountryIso2(code: string | null | undefined): boolean {
  return !!code && PHONE_COUNTRY_ISO2_SET.has(code.trim().toUpperCase());
}

/**
 * NANP area codes, for the label and the search only — **never for the input value**.
 *
 * Twelve countries share `+1`, so the dial code alone cannot tell Antigua from Barbados in a list. The area
 * code disambiguates them visually. It must not reach the field's value: the maskito mask is generated from
 * the country's libphonenumber metadata and expects the bare `+1`, with the area code typed as part of the
 * number. The source JSON spells these as `"+1 268"`, which is why it is parsed apart rather than used
 * verbatim.
 *
 * The US and Canada carry no entry: `+1` is theirs at the country level, and the flag already separates them.
 */
export const PHONE_AREA_CODE: Readonly<Record<string, string>> = {
  AG: "268",
  BS: "242",
  BB: "246",
  DM: "767",
  GD: "473",
  JM: "876",
  KN: "869",
  LC: "758",
  VC: "784",
  TT: "868",
};

export interface PhoneCountryOption {
  iso2: string;
  /** Written into the input on selection. Bare prefix, never with an area code. */
  dialCode: string;
  /** Rendered next to the dial code and matched by the search. Only where `+1` is shared. */
  areaCode?: string;
  /** Country name in the requested locale, falling back to the catalog's English name. */
  name: string;
}

const CATALOG_BY_ISO2 = new Map(ALL_PHONE_COUNTRIES.map((c) => [c.iso2, c]));

/**
 * One `Intl.DisplayNames` per locale.
 *
 * Constructing it is the expensive half — resolving the locale and loading its region table — while `.of()`
 * is a lookup in what that gave back. A fresh instance per country meant ~250 constructions for one render of
 * `ColocaCountryField`, and 125 for every keystroke in the phone picker's search box.
 *
 * `null` is a cached answer, not a miss: an unsupported locale tag throws on construction, and this keeps it
 * from throwing again on the next country.
 */
const DISPLAY_NAMES_BY_LOCALE = new Map<string, Intl.DisplayNames | null>();

function displayNamesFor(locale: string): Intl.DisplayNames | null {
  if (DISPLAY_NAMES_BY_LOCALE.has(locale)) return DISPLAY_NAMES_BY_LOCALE.get(locale) ?? null;
  let instance: Intl.DisplayNames | null = null;
  try {
    instance = new Intl.DisplayNames([locale], { type: "region" });
  } catch {
    instance = null;
  }
  DISPLAY_NAMES_BY_LOCALE.set(locale, instance);
  return instance;
}

/**
 * The ICU name for a country, or `null` when the runtime has none.
 *
 * Split from `localizedCountryName` because the two callers ask different questions: a list renderer wants a
 * name whatever it takes, while a caller that already holds its own label wants to know whether ICU had
 * anything at all — and can only prefer its own label if that "nothing" is distinguishable.
 *
 * `Intl.DisplayNames` reports it by echoing the input code back, which is what this collapses into `null`;
 * rendering it would put a bare "AQ" in the list.
 */
export function icuCountryName(iso2: string, locale: string): string | null {
  const names = displayNamesFor(locale);
  if (!names) return null;
  try {
    const display = names.of(iso2);
    return display && display !== iso2 ? display : null;
  } catch {
    return null; // `of()` throws on a malformed region code, unlike an unknown but well-formed one
  }
}

/**
 * Localized country name, falling back to the catalog's English one and, for a country the catalog does not
 * carry either, to the code. Use `icuCountryName` when you hold a better fallback than this one.
 */
export function localizedCountryName(iso2: string, locale: string): string {
  return icuCountryName(iso2, locale) ?? CATALOG_BY_ISO2.get(iso2)?.name ?? iso2;
}

const OPTIONS_BY_LOCALE = new Map<string, readonly PhoneCountryOption[]>();

/**
 * The selectable phone countries, in display order, named in `locale`.
 *
 * Regions keep their product order; inside each one the countries are sorted by their localized name, with
 * the home market pinned first. Memoized per locale — the result is stable for a given language and the list
 * is rebuilt on every render of every phone field otherwise.
 */
export function phoneCountryOptions(locale: string): readonly PhoneCountryOption[] {
  const cached = OPTIONS_BY_LOCALE.get(locale);
  if (cached) return cached;

  const collator = new Intl.Collator(locale, { sensitivity: "base" });
  const options = PHONE_COUNTRY_REGIONS.flatMap((region) =>
    region.iso2
      .map((iso2) => {
        const entry = CATALOG_BY_ISO2.get(iso2);
        if (!entry) return null;
        const areaCode = PHONE_AREA_CODE[iso2];
        return {
          iso2,
          dialCode: entry.dialCode,
          ...(areaCode ? { areaCode } : {}),
          name: localizedCountryName(iso2, locale),
        } satisfies PhoneCountryOption;
      })
      .filter((option): option is PhoneCountryOption => option !== null)
      .sort((a, b) => {
        if (a.iso2 === HOME_MARKET_ISO2) return -1;
        if (b.iso2 === HOME_MARKET_ISO2) return 1;
        return collator.compare(a.name, b.name);
      }),
  );

  OPTIONS_BY_LOCALE.set(locale, options);
  return options;
}

/**
 * What a country has to carry to be searchable. `PhoneCountryOption` and the catalog's own `PhoneCountry`
 * both satisfy it, which is what lets the three country pickers share one predicate.
 */
export interface SearchableCountry {
  iso2: string;
  dialCode: string;
  name: string;
  areaCode?: string;
}

/**
 * Whether a country matches what was typed in a picker's search box. **The predicate for all three** — the
 * phone field, `ColocaCountryField` and settings' `AccountCountrySelect` — which until now each carried their
 * own copy of a `name.toLowerCase().includes(q)`.
 *
 * Matches the localized name, the catalog's English name (so "Germany" finds Alemania for someone who knows
 * the English one), the ISO code, the dial code and the area code — including the `+1 268` spelling a person
 * reads off the list and retypes.
 *
 * **Both sides are folded** (`foldForSearch`). Since the names come from `Intl.DisplayNames` they carry the
 * locale's diacritics, and nobody types them: "panama", "mexico", "peru", "japon" and "belgica" found nothing
 * at all. The ISO code is compared whole rather than by substring — on a two-letter code a one-letter query
 * would otherwise "match" a third of the catalog, and a country whose NAME contains that letter is returned
 * by the line above anyway.
 */
export function countryMatchesQuery(country: SearchableCountry, query: string): boolean {
  const q = foldForSearch(query.trim());
  if (!q) return true;
  if (foldForSearch(country.name).includes(q)) return true;
  const english = CATALOG_BY_ISO2.get(country.iso2)?.name;
  if (english && foldForSearch(english).includes(q)) return true;
  if (country.iso2.toLowerCase() === q) return true;
  if (country.dialCode.includes(q)) return true;
  if (!country.areaCode) return false;
  return country.areaCode.includes(q) || `${country.dialCode} ${country.areaCode}`.includes(q);
}

/**
 * The same shape for a country outside the list, so a stored value stays visible and editable.
 *
 * The residence allowlist has `withPreservedCountry` for exactly this; a phone field needs it more, because
 * an unresolved country leaves its input disabled rather than merely blank.
 */
export function phoneCountryOptionFor(iso2: string, locale: string): PhoneCountryOption | null {
  const entry = CATALOG_BY_ISO2.get(iso2.trim().toUpperCase());
  if (!entry) return null;
  const areaCode = PHONE_AREA_CODE[entry.iso2];
  return {
    iso2: entry.iso2,
    dialCode: entry.dialCode,
    ...(areaCode ? { areaCode } : {}),
    name: localizedCountryName(entry.iso2, locale),
  };
}
